import React, { useContext, useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import StudentAvatar from '../StudentAvatar';
import Footer from './Footer';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  Briefcase, 
  FileText, 
  CheckSquare, 
  CreditCard, 
  PieChart, 
  ShieldCheck, 
  Globe,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  FileDown,
  Settings
} from 'lucide-react';

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [notices, setNotices] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [unseenNotices, setUnseenNotices] = useState(0);


  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [tasksRes, noticesRes] = await Promise.all([
          api.get('/tasks'),
          api.get('/requirements?is_document=false')
        ]);
        setTasks(tasksRes.data.filter(t => t.status === 'pending' || t.status === 'in-progress'));
        
        const allNotices = noticesRes.data;
        allNotices.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setNotices(allNotices);
        
        const readIds = JSON.parse(localStorage.getItem('readNoticeIds') || '[]');
        const unseen = allNotices.filter(n => !readIds.includes(n.id)).length;
        setUnseenNotices(unseen);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  useEffect(() => {
    const syncReadStatus = () => {
      const readIds = JSON.parse(localStorage.getItem('readNoticeIds') || '[]');
      setUnseenNotices(notices.filter(n => !readIds.includes(n.id)).length);
    };

    window.addEventListener('noticeReadUpdate', syncReadStatus);
    return () => {
      window.removeEventListener('noticeReadUpdate', syncReadStatus);
    };
  }, [notices]);

  useEffect(() => {
    const handleDocumentClick = () => {
      setShowProfileDropdown(false);
      setShowNotifications(false);
    };
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  const handleNotificationClick = (e) => {
    e.stopPropagation();
    setShowNotifications(!showNotifications);
    setShowProfileDropdown(false);
  };

  const handleProfileClick = (e) => {
    e.stopPropagation();
    setShowProfileDropdown(!showProfileDropdown);
    setShowNotifications(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinks = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, color: 'text-blue-500' },
    { name: 'Students', path: '/students', icon: Users, color: 'text-sky-500' },
    { name: 'Student Classes', path: '/student-classes', icon: BookOpen, color: 'text-teal-500' },
    { name: 'Working Classes', path: '/working-classes', icon: Briefcase, color: 'text-orange-500' },
    { name: 'Class Management', path: '/class-management', icon: Settings, color: 'text-slate-500' },
    { name: 'IELTS Booking', path: '/ielts', icon: BookOpen, color: 'text-purple-500' },
    { name: 'Applications', path: '/applications', icon: FileText, color: 'text-cyan-500' },
    { name: 'Tasks', path: '/tasks', icon: CheckSquare, color: 'text-violet-500' },
    { name: 'Notifications', path: '/notifications', icon: Bell, color: 'text-pink-500' },
    { name: 'Payments', path: '/payments', icon: CreditCard, color: 'text-rose-500' },
    { name: 'Reports', path: '/reports', icon: PieChart, color: 'text-amber-600' },
    { name: 'Export Students', path: '/export-students', icon: FileDown, color: 'text-emerald-600' },
    { name: 'Staff Auth', path: '/users', icon: ShieldCheck, color: 'text-blue-600' },
    { name: 'Countries', path: '/countries', icon: Globe, color: 'text-teal-600' },
  ];

  const filteredLinks = navLinks.filter(link => {
    if ((link.name === 'Reports' || link.name === 'Staff Auth' || link.name === 'Countries') && user?.role !== 'Admin') return false;
    return true;
  });

  const SidebarContent = () => (
    <>
      <div className={`p-6 flex items-center gap-3 border-b border-gray-100 transition-all duration-300 ${isCollapsed ? 'justify-center px-2' : ''}`}>
        <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="w-12 h-12 object-contain shrink-0" />
        {!isCollapsed && (
          <div className="flex flex-col animate-in fade-in duration-300">
            <h2 className="text-sm font-extrabold tracking-tight text-gray-900 leading-none">C.S.K</h2>
            <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mt-0.5">Language & Consultancy P. Ltd.</p>
          </div>
        )}
      </div>
 
      <nav className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-hide">
        {filteredLinks.map((link) => {
          const Icon = link.icon;
          const isActive = location.pathname === link.path;
          return (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`group flex items-center gap-3 px-4 py-2.5 rounded-xl border border-transparent transition-all duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${
                isActive 
                  ? 'bg-blue-50 text-blue-700 border-blue-100/50 shadow-sm font-semibold' 
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
              } ${isCollapsed ? 'justify-center px-0 w-12 mx-auto' : ''}`}
              title={isCollapsed ? link.name : ''}
              aria-current={isActive ? 'page' : undefined}
            >
              <div className={`p-1.5 rounded-lg transition-colors shrink-0 ${isActive ? 'bg-white shadow-sm' : 'group-hover:bg-white'}`}>
                <Icon size={18} className={isActive ? link.color : 'text-slate-400 group-hover:text-slate-650'} />
              </div>
              {!isCollapsed && <span className="text-sm font-semibold flex-1 truncate animate-in slide-in-from-left-2 duration-300">{link.name}</span>}
              {isActive && !isCollapsed && <div className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />}
            </Link>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F1F5F9]">
      {/* Desktop Sidebar */}
      <aside className={`${isCollapsed ? 'w-20' : 'w-64'} bg-white border-r border-gray-200 flex flex-col hidden md:flex transition-all duration-300 ease-in-out relative group/sidebar`}>

        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm z-40 md:hidden animate-in fade-in duration-200"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Sidebar */}
      <aside className={`fixed inset-y-0 left-0 w-72 bg-white flex flex-col z-50 shadow-2xl transform transition-transform duration-300 ease-out md:hidden ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button 
          onClick={() => setIsMobileMenuOpen(false)}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-all"
          aria-label="Close navigation menu"
        >
          <X size={20} />
        </button>
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {/* Topbar */}
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-100 flex items-center justify-between px-6 z-30">
            <div className="flex items-center gap-4">
               <button 
                 onClick={() => setIsMobileMenuOpen(true)} 
                 className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-all"
                 aria-label="Open navigation menu"
               >
                 <Menu size={20} />
               </button>
               {/* Desktop Toggle Alternative */}
               <button 
                 onClick={() => setIsCollapsed(!isCollapsed)} 
                 className="hidden md:flex p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                 title="Toggle Sidebar"
                 aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                 aria-expanded={!isCollapsed}
               >
                 {isCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
               </button>
               <div className="h-6 w-[1px] bg-slate-100 hidden md:block"></div>
               <div className="hidden sm:flex flex-col">
                  <h1 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    System <ChevronRight size={10} /> <span className="text-blue-600">{location.pathname === '/' ? 'Overview' : location.pathname.substring(1).replace('-', ' ')}</span>
                  </h1>
                  <p className="text-base font-black text-slate-900">Welcome back, {user?.full_name || user?.username} 👋</p>
               </div>
            </div>
            <div className="flex items-center gap-4">
                 <div className="relative">
                   <button 
                     onClick={handleNotificationClick}
                     className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all relative focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
                     aria-label="Toggle notifications menu"
                     aria-expanded={showNotifications}
                   >
                      <Bell size={20} />
                      {(tasks.length > 0 || unseenNotices > 0) && (
                        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white"></span>
                      )}
                   </button>
                    {showNotifications && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl shadow-blue-900/10 border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
                        <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                          <h3 className="text-sm font-black text-gray-900 text-left">Notifications</h3>
                        </div>
                        <div className="p-2">
                          <Link 
                            to="/tasks" 
                            onClick={() => setShowNotifications(false)}
                            className="flex items-center justify-between p-3 hover:bg-blue-50 rounded-xl transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                <CheckSquare size={16} />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-bold text-gray-900 group-hover:text-blue-700">Tasks</p>
                                <p className="text-[10px] font-bold text-gray-500">{tasks.length} Pending</p>
                              </div>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500" />
                          </Link>

                          <Link 
                            to="/notifications" 
                            onClick={() => setShowNotifications(false)}
                            className="flex items-center justify-between p-3 hover:bg-rose-50 rounded-xl transition-colors group mt-1"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center group-hover:bg-rose-600 group-hover:text-white transition-colors">
                                <FileText size={16} />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-bold text-gray-900 group-hover:text-rose-700">Notices</p>
                                <p className="text-[10px] font-bold text-gray-500">{unseenNotices} Unseen</p>
                              </div>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 group-hover:text-rose-500" />
                          </Link>
                        </div>
                      </div>
                    )}
                 </div>
                 <div className="h-8 w-[1px] bg-gray-100 mx-1"></div>
                 <div className="relative">
                   <button 
                     onClick={handleProfileClick}
                     className="flex items-center gap-3 hover:bg-gray-50 p-1 rounded-xl transition-all group"
                   >
                     <div className="hidden sm:block text-right">
                       <p className="text-xs font-bold text-gray-900 leading-none group-hover:text-blue-600 transition-colors">
                         {user?.full_name || user?.username}
                       </p>
                       <p className="text-[10px] text-gray-500 font-bold uppercase mt-1 leading-none">
                         {user?.role}
                       </p>
                     </div>
                      <StudentAvatar 
                        photoUrl={user?.photo_url} 
                        fullName={user?.full_name || user?.username} 
                        className="w-9 h-9 rounded-xl" 
                      />
                   </button>
                   
                    {showProfileDropdown && (
                      <div 
                        className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl shadow-blue-900/10 border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="p-2 space-y-1">
                          {user?.role === 'Admin' && (
                            <>
                              <Link 
                                to="/profile" 
                                onClick={() => setShowProfileDropdown(false)}
                                className="flex items-center gap-3 p-3 hover:bg-blue-50 rounded-xl transition-colors group text-gray-700 hover:text-blue-700 text-sm font-bold"
                              >
                                <Settings size={16} className="text-gray-400 group-hover:text-blue-600 shrink-0" />
                                <span>View Profile</span>
                              </Link>
                              
                              <hr className="border-gray-100 my-1" />
                            </>
                          )}
                          
                          <button 
                            onClick={handleLogout} 
                            className="w-full flex items-center gap-3 p-3 hover:bg-rose-50 text-red-600 rounded-xl transition-colors text-sm font-bold text-left"
                          >
                            <LogOut size={16} className="shrink-0" />
                            <span>Logout</span>
                          </button>
                        </div>
                      </div>
                    )}
                 </div>
            </div>
        </header>

        <div className="flex-1 overflow-y-auto scroll-smooth flex flex-col">
          <div className="flex-1 p-6 md:p-8">
            <Outlet />
          </div>
          <Footer />
        </div>
      </main>
    </div>
  );
};

export default Layout;
